<div class="container">
  <header class="header">
    <button class="back-button" (click)="goBack()">
      <span class="back-arrow">←</span>
    </button>
    <div class="d-flex flex-column">
      <h1 class="header-text">Safety Policy &amp; Law Enforcement Operational Guidelines</h1>
      <span class="header-subtitle">Child Safety Standards (CSAE) &amp; Protocols for Receiving, Validating, and Responding to Law Enforcement Inquiries</span>
    </div>
  </header>

  <div class="content">
    <p class="last-updated">Last Updated: {{ lastUpdated }}</p>

    <!-- SECTION 1: COMMITMENT -->
    <section class="section">
      <h2 class="heading">1. Our Commitment to User Safety</h2>
      <p class="paragraph">
        Adore The Balloon (operated by Insizon LLC, "we," "our," or "us") is dedicated to providing a secure, respectful, and legally compliant online dating community. We enforce a zero-tolerance policy against child sexual abuse and exploitation (CSAE), harassment, exploitation, violence, and all illegal activity.
      </p>
      <p class="paragraph">
        To fulfill this commitment, we maintain proactive safety controls, robust content moderation, and structured legal and operational protocols governing our cooperation with authorized law enforcement agencies while upholding legitimate user privacy protections under applicable law.
      </p>
    </section>

    <!-- SECTION 2: AGE RESTRICTIONS -->
    <section class="section">
      <h2 class="heading">2. Age Restrictions and Verification</h2>

      <h3 class="sub-heading">Minimum Age Requirement</h3>
      <p class="paragraph">
        <b>Users must be at least 18 years of age</b> to register, access, or use Adore The Balloon. Minors are strictly prohibited from using the platform.
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">We strictly enforce this age threshold during account creation and profile setup.</li>
        <li class="bullet-point">Date of birth is validated upon signup; accounts indicating an age under 18 are rejected automatically.</li>
        <li class="bullet-point">Profiles discovered or reported to belong to underage individuals are immediately suspended and purged after evidence preservation.</li>
      </ul>

      <h3 class="sub-heading">Verification Methods</h3>
      <ul class="bullet-list">
        <li class="bullet-point"><b>Self-Declaration &amp; Age Gate:</b> Affirmative confirmation of majority age (18+) required before profile setup.</li>
        <li class="bullet-point"><b>Phone Number Verification:</b> SMS one-time password (OTP) verification binds accounts to verified carrier numbers.</li>
        <li class="bullet-point"><b>Email Authentication:</b> Email verification is required for all registered users.</li>
        <li class="bullet-point"><b>Identity Verification (KYC):</b> High-risk activity triggers secondary photo identification and selfie verification checks.</li>
      </ul>
    </section>

    <!-- SECTION 3: CONTENT MODERATION -->
    <section class="section">
      <h2 class="heading">3. Content Moderation and Safety Measures</h2>

      <h3 class="sub-heading">Prohibited Content</h3>
      <p class="paragraph">We strictly prohibit:</p>
      <ul class="bullet-list">
        <li class="bullet-point">Depicting, promoting, soliciting, or facilitating child sexual abuse material (CSAM) or child sexual exploitation and abuse (CSAE).</li>
        <li class="bullet-point">Involving minors in any romantic, sexual, or suggestive context.</li>
        <li class="bullet-point">Grooming, solicitation, or predatory contact directed at minors.</li>
        <li class="bullet-point">Non-consensual sexual content, revenge pornography, non-consensual intimate imagery (NCII), and extortion.</li>
        <li class="bullet-point">Hate speech, threats of violence, illegal commerce, and dangerous organizations.</li>
      </ul>

      <h3 class="sub-heading">Monitoring and Enforcement Systems</h3>
      <ul class="bullet-list">
        <li class="bullet-point"><b>Human Review Team:</b> Dedicated moderation personnel evaluate reported content, profile assets, and flagged accounts.</li>
        <li class="bullet-point"><b>Media Moderation:</b> Uploaded profile pictures and public media are reviewed for policy violations before display.</li>
        <li class="bullet-point"><b>Real-Time Behavioral Flags:</b> Automated detection rules surface suspicious messaging patterns, velocity spikes, and duplicate accounts.</li>
        <li class="bullet-point"><b>In-App Reporting Tools:</b> Accessible report and block mechanisms are present on every profile and conversation view.</li>
      </ul>
    </section>

    <!-- SECTION 4: CSAE/CSAM REPORTING -->
    <section class="section">
      <h2 class="heading">4. Standards Against Child Sexual Abuse and Exploitation (CSAE)</h2>
      <p class="paragraph">
        Adore The Balloon enforces strict compliance with Title 18 of the United States Code, including 18 U.S.C. § 2258A (mandatory reporting of child sexual abuse material), the PROTECT Act, and international child protection statutes.
      </p>

      <div class="protocol-card warning">
        <strong style="color: #dc3545;">MANDATORY CSAM REPORTING PROTOCOL:</strong>
        <p class="paragraph" style="margin-top: 6px;">
          Upon obtaining actual knowledge or reasonable suspicion of apparent child sexual abuse material (CSAM) or child sexual exploitation, we execute the following steps without delay:
        </p>
        <ul class="bullet-list">
          <li class="bullet-point"><b>Immediate Content Removal:</b> The offending media is instantly quarantined and taken offline to prevent dissemination.</li>
          <li class="bullet-point"><b>Account Termination:</b> The associated account is permanently banned, and linked identifiers (device IDs, phone numbers, IP addresses) are blocked.</li>
          <li class="bullet-point"><b>Evidence Preservation:</b> Digital evidence, IP logs, account metadata, and media hashes are secured in restricted-access storage pursuant to 18 U.S.C. § 2258A(h).</li>
          <li class="bullet-point"><b>Electronic Report to NCMEC:</b> We file a comprehensive electronic CyberTipline report with the <b>National Center for Missing &amp; Exploited Children (NCMEC)</b> containing all relevant identifiers, media hashes, timestamps, and connection records.</li>
          <li class="bullet-point"><b>Law Enforcement Referral:</b> We actively cooperate with designated ICAC (Internet Crimes Against Children) task forces, federal agencies (FBI, HSI), and local law enforcement investigating the referral.</li>
        </ul>
      </div>
    </section>

    <!-- SECTION 5: LAW ENFORCEMENT OPERATIONAL & LEGAL PROTOCOLS (STRIPE REQUIREMENT) -->
    <section class="section">
      <h2 class="heading">5. Law Enforcement Operational &amp; Legal Protocols</h2>
      <p class="paragraph">
        Adore The Balloon is operated by Insizon LLC in the United States. We manage government and law enforcement requests in accordance with the <b>Electronic Communications Privacy Act (ECPA)</b> / <b>Stored Communications Act (SCA), 18 U.S.C. § 2701 et seq.</b>, the Fourth Amendment to the United States Constitution, and other applicable federal, state, and international laws.
      </p>
      <p class="paragraph">
        We handle law enforcement inquiries only after appropriate verification and legal review. This section outlines our operational and legal protocols for <b>receiving</b>, <b>validating</b>, and <b>responding to</b> inquiries and legal process served by authorized law enforcement agencies.
      </p>

      <!-- PHASE 1: RECEIVING -->
      <div class="protocol-card highlight">
        <span class="protocol-badge badge-subpoena">Protocol Phase 1</span>
        <h3 class="sub-heading" style="margin-top: 4px; color: #0d6efd;">Receiving Law Enforcement Inquiries (Operational Ingestion)</h3>
        <p class="paragraph">
          To ensure security, proper chain of custody, and prompt routing, all law enforcement inquiries, preservation letters, subpoenas, court orders, and warrants should be submitted through our designated operational channels:
        </p>
        <ul class="bullet-list">
          <li class="bullet-point">
            <b>Official Email Ingestion:</b> Formal submissions should be transmitted electronically to <b>business&#64;insizon.com</b> (with copy to <b>legal&#64;adoretheballoon.com</b>).
          </li>
          <li class="bullet-point">
            <b>Subject Line Standard:</b> The subject line should indicate the nature of the request (e.g., <code>[Law Enforcement Request - Case #12345]</code> or <code>[URGENT: Emergency Disclosure Request]</code>).
          </li>
          <li class="bullet-point">
            <b>Transmission Channel &amp; Agency Verification:</b> Submissions should ideally originate from an official government or law enforcement agency email domain (e.g., <code>.gov</code>, <code>.police.uk</code>, <code>.usdoj.gov</code>, or official department domain). Requests received through other channels are not rejected automatically; instead, they require secondary independent agency and officer authentication before any review or processing occurs.
          </li>
          <li class="bullet-point">
            <b>Physical Service of Legal Process:</b>
            <div class="notice-box" style="margin: 8px 0;">
              <strong>Insizon LLC</strong><br>
              Attn: Legal &amp; Law Enforcement Compliance Department<br>
              Operational Address: [Mailing address / Registered agent on file]<br>
              Direct Legal Ingestion: business&#64;insizon.com
            </div>
          </li>
          <li class="bullet-point">
            <b>Required Submission Details:</b> To enable proper processing, law enforcement submissions must provide:
            <ol style="margin: 6px 0 0 20px; padding: 0; font-size: 13px; line-height: 20px; color: #475569;">
              <li>Requesting official's full name, rank or title, badge/ID number, agency/department, and official telephone number.</li>
              <li>Official mailing address and verified official agency email address.</li>
              <li>Statutory authority and nature of the criminal investigation or proceeding.</li>
              <li>Relevant case number, investigation docket, or court docket reference.</li>
              <li>Clear, particularized account identifiers (e.g., registered email address, verified phone number, user ID, profile URL, or transaction reference). General, non-particularized queries cannot be processed.</li>
              <li>The specific categories of records sought and a narrowly defined date or time range.</li>
            </ol>
          </li>
          <li class="bullet-point">
            <b>Intake Logging:</b> Upon receipt, formal inquiries are logged into our compliance tracking workflow with timestamp of receipt, tracking reference, requesting agency information, and assigned compliance personnel.
          </li>
        </ul>
      </div>

      <!-- PHASE 2: VALIDATING -->
      <div class="protocol-card highlight">
        <span class="protocol-badge badge-order">Protocol Phase 2</span>
        <h3 class="sub-heading" style="margin-top: 4px; color: #0d6efd;">Validating Law Enforcement Inquiries (Legal Review &amp; Verification)</h3>
        <p class="paragraph">
          Before any user records are retrieved or disclosed, every inquiry undergoes two essential verification stages: <b>Requestor Authentication</b> and <b>Legal Authority Verification</b>.
        </p>

        <h4 style="font-size: 14px; font-weight: 700; color: #1e293b; margin: 12px 0 6px 0;">A. Requestor Authentication</h4>
        <ul class="bullet-list">
          <li class="bullet-point">We verify the requesting officer's identity, agency affiliation, and official authority.</li>
          <li class="bullet-point">Verification is conducted through independent means, such as public department switchboards, official agency directories, or domain verification.</li>
        </ul>

        <h4 style="font-size: 14px; font-weight: 700; color: #1e293b; margin: 12px 0 6px 0;">B. Legal Instrument Validation Matrix</h4>
        <p class="paragraph">
          Under the Stored Communications Act (18 U.S.C. §§ 2701–2712) and applicable constitutional standards, different categories of subscriber data require specific tiers of legal authorization:
        </p>

        <div class="policy-table-wrapper">
          <table class="policy-table">
            <thead>
              <tr>
                <th style="width: 25%;">Category of Information</th>
                <th style="width: 25%;">Required Legal Authority</th>
                <th style="width: 25%;">Statutory Basis</th>
                <th style="width: 25%;">Scope of Records Disclosable</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>Basic Subscriber Information (BSI)</b></td>
                <td><span class="protocol-badge badge-subpoena">Subpoena</span><br>Grand Jury, Administrative, or Trial Subpoena</td>
                <td>18 U.S.C. § 2703(c)(2)</td>
                <td>Account name, email address, phone number, registration timestamp, account creation IP, and login/access IP timestamps.</td>
              </tr>
              <tr>
                <td><b>Non-Content Records &amp; Metadata</b></td>
                <td><span class="protocol-badge badge-order">Court Order</span><br>Judicial 2703(d) Order based on specific &amp; articulable facts</td>
                <td>18 U.S.C. § 2703(d)</td>
                <td>Historical usage logs, transactional metadata (dates/times of matches, likes), profile edits, account status history. Excludes communication content.</td>
              </tr>
              <tr>
                <td><b>Stored Content &amp; Communications</b></td>
                <td><span class="protocol-badge badge-warrant">Search Warrant</span><br>Signed by a Judicial Magistrate on Probable Cause</td>
                <td>18 U.S.C. § 2703(a); Fed. R. Crim. P. 41</td>
                <td>Direct messages, private photos/media uploads, voice recordings, bio drafts, and stored user communications.</td>
              </tr>
              <tr>
                <td><b>Imminent Harm / Life Safety</b></td>
                <td><span class="protocol-badge badge-emergency">Emergency Disclosure</span><br>Written Emergency Request meeting statutory criteria</td>
                <td>18 U.S.C. § 2702(b)(8) &amp; § 2702(c)(4)</td>
                <td>Records strictly necessary to prevent imminent death or serious physical injury to any person.</td>
              </tr>
              <tr>
                <td><b>Record Preservation</b></td>
                <td><span class="protocol-badge badge-subpoena">Preservation Letter</span><br>Written 2703(f) request pending legal process</td>
                <td>18 U.S.C. § 2703(f)</td>
                <td>Preservation of existing records for 90 days. Records are not disclosed until valid legal process is served and verified.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h4 style="font-size: 14px; font-weight: 700; color: #1e293b; margin: 12px 0 6px 0;">C. Scope, Jurisdiction &amp; Legal Review</h4>
        <ul class="bullet-list">
          <li class="bullet-point"><b>Competent Jurisdiction:</b> Legal process must be issued by a court or authority possessing competent jurisdiction.</li>
          <li class="bullet-point"><b>Facial Validity:</b> The instrument must be signed by an authorized judicial officer, clerk, or prosecutor, must not be expired, and must clearly identify the subject account.</li>
          <li class="bullet-point"><b>Overbroad or Vague Requests:</b> If an inquiry is ambiguous, overbroad, or legally deficient, Adore The Balloon contacts the requesting agency to seek clarification, require an amended legal instrument, or formally object where appropriate under applicable law.</li>
        </ul>
      </div>

      <!-- PHASE 3: RESPONDING -->
      <div class="protocol-card highlight">
        <span class="protocol-badge badge-warrant">Protocol Phase 3</span>
        <h3 class="sub-heading" style="margin-top: 4px; color: #0d6efd;">Responding to Law Enforcement Inquiries (Operational Production)</h3>
        <p class="paragraph">
          Once an inquiry is fully verified and legally validated, fulfillment is handled under strict operational controls:
        </p>
        <ul class="bullet-list">
          <li class="bullet-point">
            <b>Restricted Extraction:</b> Data retrieval is performed exclusively by authorized, authenticated technical personnel in coordination with compliance reviewers.
          </li>
          <li class="bullet-point">
            <b>Principle of Data Minimization:</b> We disclose only the information legally authorized for production under the validated legal instrument. Unrelated user data, communications of uninvolved third parties, and records outside the specified date range are excluded or redacted.
          </li>
          <li class="bullet-point">
            <b>Secure Delivery:</b> Responsive records are compiled and transmitted via secure, encrypted electronic means or directly through verified official law enforcement digital evidence transfer systems.
          </li>
          <li class="bullet-point">
            <b>Response Timelines:</b> Requests are processed as promptly as feasible, taking into account the complexity of the inquiry, the volume of records sought, the necessity of legal review and verification, and our operational capacity. Requests involving imminent threats to life or serious physical injury are prioritized for expedited handling.
          </li>
          <li class="bullet-point">
            <b>Audit Logging:</b> We maintain records of all inquiries received, the legal basis, supervisory approval, data produced, recipient officer, and date of transmission for compliance and statutory audit purposes.
          </li>
        </ul>
      </div>
    </section>

    <!-- SECTION 6: EMERGENCY DISCLOSURE PROTOCOL -->
    <section class="section">
      <h2 class="heading">6. Emergency Disclosure Protocol (Exigent Circumstances)</h2>
      <p class="paragraph">
        Under <b>18 U.S.C. § 2702(b)(8)</b> and <b>18 U.S.C. § 2702(c)(4)</b>, Adore The Balloon may disclose subscriber records or communications to law enforcement if we have a good-faith belief that an emergency involving imminent danger of death or serious physical injury to any person requires disclosure without delay of information relating to the emergency.
      </p>

      <div class="notice-box">
        <strong>EMERGENCY SUBMISSION REQUIREMENTS FOR LAW ENFORCEMENT:</strong><br>
        Officers submitting an Emergency Disclosure Request (EDR) must send an email to <b>business&#64;insizon.com</b> with the subject line <code>[URGENT: Emergency Disclosure Request]</code> containing:
        <ul style="margin: 6px 0 0 16px; padding: 0;">
          <li>Detailed factual explanation of the imminent threat of death or serious physical injury.</li>
          <li>Identity and safety status of the individual(s) in immediate danger.</li>
          <li>Specific account identifier(s) (email, phone, user ID, profile URL).</li>
          <li>The narrow categories of records needed to avert or respond to the immediate harm.</li>
          <li>Justification explaining why obtaining judicial legal process in advance is impossible given the temporal emergency.</li>
          <li>Officer's full name, rank, badge number, official agency, direct telephone number, and official agency email.</li>
        </ul>
      </div>

      <p class="paragraph">
        Emergency requests involving imminent danger to life or serious physical harm are prioritized and reviewed on an expedited basis upon receipt. If validated, responsive records strictly proportional to mitigating the emergency are produced.
      </p>
    </section>

    <!-- SECTION 7: DATA PRESERVATION PROTOCOL -->
    <section class="section">
      <h2 class="heading">7. Data Preservation Requests (18 U.S.C. § 2703(f))</h2>
      <p class="paragraph">
        Pursuant to <b>18 U.S.C. § 2703(f)</b>, authorized law enforcement agencies may request the temporary preservation of stored user records and communications pending the issuance of a formal subpoena, court order, or search warrant.
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">
          <b>Implementation:</b> Upon receiving a formal 2703(f) preservation letter on official agency letterhead, we take reasonable operational steps to preserve existing account data and logs to prevent automatic deletion or user purging.
        </li>
        <li class="bullet-point">
          <b>90-Day Retention Window:</b> Preserved records are held in secure quarantine for an initial statutory duration of <b>90 days</b>.
        </li>
        <li class="bullet-point">
          <b>Extension:</b> Law enforcement may request one 90-day extension (for a total of 180 days) upon formal written renewal prior to expiration.
        </li>
        <li class="bullet-point">
          <b>Custody without Disclosure:</b> Preserved records are kept in secure escrow and are <b>not disclosed</b> to law enforcement until the required formal legal process (e.g., search warrant or 2703(d) order) is served, verified, and validated.
        </li>
        <li class="bullet-point">
          <b>Expiration:</b> If formal legal process is not served before the preservation period expires, preserved records are released back to standard data retention and deletion schedules.
        </li>
      </ul>
    </section>

    <!-- SECTION 8: USER NOTIFICATION & NON-DISCLOSURE ORDERS -->
    <section class="section">
      <h2 class="heading">8. User Notification Policy and Non-Disclosure Orders</h2>
      <p class="paragraph">
        <b>General Transparency Policy:</b> Adore The Balloon respects user privacy and procedural rights. Our standard policy is to notify affected users when we receive legal process requesting their account records prior to disclosure, providing an opportunity for the user to seek protective legal remedies (such as a motion to quash).
      </p>

      <h3 class="sub-heading">Exceptions to User Notice</h3>
      <p class="paragraph">
        User notice will <b>NOT</b> be provided under the following legally recognized circumstances:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">
          <b>Court Non-Disclosure Orders (Gag Orders):</b> When the legal process is accompanied by an official non-disclosure order issued under <b>18 U.S.C. § 2705(b)</b> or state equivalent, enjoining user notice for a specified period upon a judicial finding of potential endangerment or evidence destruction.
        </li>
        <li class="bullet-point">
          <b>Child Exploitation &amp; CSAE:</b> Notice is strictly prohibited and never given in matters involving child sexual abuse material or child exploitation investigations under 18 U.S.C. § 2258A.
        </li>
        <li class="bullet-point">
          <b>Imminent Life Safety:</b> Where notice would create an imminent risk of death, physical injury, or flight from prosecution.
        </li>
        <li class="bullet-point">
          <b>Statutory Preclusion:</b> Where user notice is prohibited by applicable statute.
        </li>
      </ul>
      <p class="paragraph">
        Upon the formal expiration or dissolution of a judicial non-disclosure order, we reserve the right to notify the affected user in accordance with applicable law.
      </p>
    </section>

    <!-- SECTION 9: INTERNATIONAL REQUESTS -->
    <section class="section">
      <h2 class="heading">9. International Law Enforcement Inquiries &amp; MLAT</h2>
      <p class="paragraph">
        Insizon LLC is a United States entity. All requests originating from international law enforcement agencies are evaluated under applicable U.S. law, international treaties, and jurisdictional requirements:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">
          <b>Mutual Legal Assistance Treaties (MLAT):</b> Foreign agencies seeking user communications or non-public records generally must submit their requests through the formal <b>MLAT</b> process or diplomatic <b>Letters Rogatory</b> coordinated via the Office of International Affairs (OIA) at the U.S. Department of Justice.
        </li>
        <li class="bullet-point">
          <b>Bilateral Agreements:</b> Where qualifying bilateral agreements are enacted under the U.S. CLOUD Act (18 U.S.C. § 2523), certified foreign authorities may submit qualifying legal process subject to statutory compliance and human rights protections.
        </li>
        <li class="bullet-point">
          <b>International Emergency Inquiries:</b> In urgent life-safety scenarios involving imminent death or grievous bodily harm, foreign law enforcement authorities may submit an Emergency Disclosure Request under 18 U.S.C. § 2702(b)(8) directly to our compliance intake.
        </li>
      </ul>
    </section>

    <!-- SECTION 10: USER SAFETY FEATURES -->
    <section class="section">
      <h2 class="heading">10. In-App User Safety Features</h2>

      <h3 class="sub-heading">Account &amp; Communication Protections</h3>
      <ul class="bullet-list">
        <li class="bullet-point"><b>Verified Profiles:</b> Enhanced badge verification ensures user legitimacy.</li>
        <li class="bullet-point"><b>Block &amp; Report:</b> Instant blocking severs all contact and prevents future matching or profile visibility.</li>
        <li class="bullet-point"><b>Media Safeguards:</b> Automated inspection screens out prohibited imagery before it can be viewed.</li>
        <li class="bullet-point"><b>Privacy Controls:</b> Granular settings allow users to manage discovery visibility and interaction preferences.</li>
      </ul>
    </section>

    <!-- SECTION 11: COST REIMBURSEMENT -->
    <section class="section">
      <h2 class="heading">11. Cost Reimbursement</h2>
      <p class="paragraph">
        Pursuant to <b>18 U.S.C. § 2706</b>, Adore The Balloon reserves the right to seek reimbursement for reasonable direct costs incurred in researching, assembling, reproducing, and delivering records in response to subpoenas, court orders, and warrants.
      </p>
      <p class="paragraph">
        We do not seek cost reimbursement in investigations concerning child sexual exploitation (CSAE/CSAM) or life-safety emergency disclosures.
      </p>
    </section>

    <!-- SECTION 12: CONTACT & LEGAL SERVICE -->
    <section class="section">
      <h2 class="heading">12. Contact Points for Law Enforcement &amp; User Safety</h2>
      <p class="paragraph">
        For formal inquiries, service of legal process, and safety escalations:
      </p>

      <div class="notice-box">
        <p style="margin: 0 0 8px 0;"><strong>Law Enforcement &amp; Legal Inquiries:</strong></p>
        <ul style="margin: 0 0 0 16px; padding: 0;">
          <li>Primary Compliance Email: <b>business&#64;insizon.com</b></li>
          <li>Secondary Legal Ingestion: <b>legal&#64;adoretheballoon.com</b></li>
          <li>Urgent Child Safety (CSAE) Escalations: <b>childsafety&#64;insizon.com</b></li>
          <li>General Support: <b>support&#64;adoretheballoon.com</b></li>
        </ul>
        <p style="margin: 8px 0 0 0;">
          <strong>Operating Entity:</strong> Insizon LLC (Operator of Adore The Balloon)<br>
          <strong>Operational Hours:</strong> Monday – Friday, 9:00 AM – 6:00 PM EST, with emergency requests involving imminent danger of death or serious physical harm prioritized for expedited review upon receipt.
        </p>
      </div>

      <p class="paragraph">
        <em>Note: Acceptance of legal process via electronic transmission is provided for convenience and does not waive any legal objections, lack of jurisdiction, or defect in service. This document outlines general operational procedures and does not create contractual or third-party rights.</em>
      </p>
    </section>
  </div>
</div>